import { AlertCircle, ArrowRight, CheckCircle2, Info, ShoppingBag, TriangleAlert, X } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../state/AppState";

const TOAST_ICON = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: TriangleAlert
} as const;

const TOAST_TITLE = {
  success: "Success",
  error: "Error",
  info: "Info",
  warning: "Warning"
} as const;

const STANDARD_TOAST_DURATION_MS = 3000;
const CART_REMINDER_DURATION_MS = 5000;

function StandardToast({
  id,
  message,
  type,
  onDismiss
}: {
  id: string;
  message: string;
  type: keyof typeof TOAST_ICON;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timeoutId = window.setTimeout(() => onDismiss(id), STANDARD_TOAST_DURATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [id, onDismiss]);

  const Icon = TOAST_ICON[type];
  const title = TOAST_TITLE[type];

  return (
    <div key={id} className={`toast toast-react toast-${type}`} role="status">
      <div className="toast-react-body">
        <span className="toast-react-icon" aria-hidden="true">
          <Icon size={18} strokeWidth={2.1} />
        </span>
        <span className="toast-react-copy">
          <strong className="toast-react-title">{title}</strong>
          <span className="toast-react-message">{message}</span>
        </span>
      </div>
      <button
        className="toast-dismiss toast-dismiss-progress"
        type="button"
        aria-label="Dismiss notification"
        style={{ ["--toast-duration" as string]: `${STANDARD_TOAST_DURATION_MS}ms` }}
        onClick={() => onDismiss(id)}
      >
        <X size={16} strokeWidth={2.2} />
      </button>
    </div>
  );
}

function CartReminderToast({
  id,
  message,
  actionLabel = "Checkout now",
  onDismiss,
  onCheckout
}: {
  id: string;
  message: string;
  actionLabel?: string;
  onDismiss: (id: string) => void;
  onCheckout: () => void;
}) {
  useEffect(() => {
    const timeoutId = window.setTimeout(() => onDismiss(id), CART_REMINDER_DURATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [id, onDismiss]);

  return (
    <button
      className="toast toast-cart-reminder"
      type="button"
      onClick={() => {
        onDismiss(id);
        onCheckout();
      }}
    >
      <div className="toast-cart-reminder-content">
        <div className="toast-cart-reminder-main">
          <ShoppingBag className="toast-cart-reminder-icon" size={18} aria-hidden="true" />
          <span className="toast-cart-reminder-text">{message}</span>
        </div>
        <div className="toast-cart-reminder-divider" />
        <div className="toast-cart-reminder-cta">
          <span>{actionLabel}</span>
          <ArrowRight size={13} aria-hidden="true" />
        </div>
      </div>
    </button>
  );
}

export function ToastStack() {
  const navigate = useNavigate();
  const { dismissToast, toasts } = useAppState();

  return (
    <div id="toast-container" className="toast-container" aria-live="polite" aria-label="Notifications">
      {toasts.map((toast) => {
        if (toast.type === "cart-reminder" || toast.type === "cart-added") {
          return (
            <CartReminderToast
              key={toast.id}
              id={toast.id}
              message={toast.message}
              actionLabel={toast.type === "cart-added" ? "Go to cart" : "Checkout now"}
              onDismiss={dismissToast}
              onCheckout={() => navigate("/cart")}
            />
          );
        }

        const type = toast.type || "info";
        return (
          <StandardToast key={toast.id} id={toast.id} message={toast.message} type={type} onDismiss={dismissToast} />
        );
      })}
    </div>
  );
}
